/*
 * Automatisch cadeau: adds a configured gift variant (qty 1, tagged with the
 * _auto_gift line-item property) when the cart holds enough items from the
 * configured trigger collections, and removes it again when the cart drops
 * below the threshold. Pricing is handled by a separate automatic Buy X Get Y
 * discount; this script only manages the line item.
 *
 * State (qualifying count, gift line key) is rendered server-side by
 * snippets/auto-gift-config.liquid and re-fetched through the Section
 * Rendering API (sections/auto-gift-state.liquid), because /cart.js line
 * items do not expose collection membership.
 *
 * Loaded from layout/theme.liquid only when settings.auto_gift_enabled.
 */

(() => {
  const SECTION_ID = 'auto-gift-state';
  const STATE_SELECTOR = 'script[data-auto-gift-state]';
  const DECLINED_KEY = 'auto-gift-declined';
  const ADDED_KEY = 'auto-gift-added';

  // sessionStorage can throw (privacy modes); treat it as best-effort.
  function readSession(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  function writeSession(key, value) {
    try {
      if (value === null) {
        window.sessionStorage.removeItem(key);
      } else {
        window.sessionStorage.setItem(key, value);
      }
    } catch {
      // ignore
    }
  }

  class AutoGift {
    constructor() {
      // Single promise queue: every check/mutation is chained onto it, so cart
      // calls never interleave and transitions are evaluated one at a time.
      this.queue = Promise.resolve();
      this.addBlocked = false;

      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'auto-gift') return;
        return this.scheduleCheck();
      });

      // Initial state is rendered inline by theme.liquid, so page load costs
      // no extra request.
      const initialState = this.readState(document);
      this.scheduleCheck(initialState);
    }

    scheduleCheck(state) {
      this.queue = this.queue.then(() => this.check(state)).catch(() => {});
      return this.queue;
    }

    readState(root) {
      const element = root.querySelector(STATE_SELECTOR);
      if (!element) return null;
      try {
        return JSON.parse(element.textContent);
      } catch {
        return null;
      }
    }

    fetchState() {
      return fetch(`${routes.cart_url}?section_id=${SECTION_ID}`)
        .then((response) => response.text())
        .then((responseText) => this.readState(new DOMParser().parseFromString(responseText, 'text/html')));
    }

    check(initialState) {
      const statePromise = initialState ? Promise.resolve(initialState) : this.fetchState();
      return statePromise
        .then((state) => {
          if (!state || !state.enabled) return;

          const variantId = parseInt(state.variantId, 10);
          if (!variantId || !(state.threshold > 0)) return;

          const giftPresent = Boolean(state.giftLineKey);
          const giftDesired = state.qualifyingQuantity >= state.threshold;

          // Campaign changed mid-session: an old tagged gift line is still in
          // the cart. Remove it; the follow-up check adds the new one.
          if (giftPresent && state.giftLineVariantId !== variantId) {
            writeSession(ADDED_KEY, null);
            return this.changeGift(state.giftLineKey, 0).then(() => {
              // Queue a follow-up check (which may add the new gift) without
              // returning its promise: that promise is chained behind the
              // current task and awaiting it here would deadlock the queue.
              this.scheduleCheck();
            });
          }

          // The gift stays free for exactly 1; clamp if the customer bumped it.
          if (giftDesired && giftPresent && state.giftLineQuantity > 1) {
            return this.changeGift(state.giftLineKey, 1);
          }

          if (giftDesired && !giftPresent) {
            // We added it earlier this session and it is gone while the cart
            // still qualifies: the customer removed it. Respect that choice
            // for the rest of the session.
            if (readSession(ADDED_KEY) === String(variantId)) {
              writeSession(DECLINED_KEY, String(variantId));
              writeSession(ADDED_KEY, null);
              return;
            }
            if (readSession(DECLINED_KEY) === String(variantId)) return;
            if (this.addBlocked) return;
            return this.addGift(variantId);
          }

          if (!giftDesired && giftPresent && state.removeBelowThreshold) {
            writeSession(ADDED_KEY, null);
            return this.changeGift(state.giftLineKey, 0);
          }

          if (!giftDesired && !giftPresent) {
            // Out of gift territory entirely: reset the added marker so a
            // fresh qualifying cart can receive the gift again.
            writeSession(ADDED_KEY, null);
          }
        })
        .catch(() => {});
    }

    addGift(variantId) {
      const body = JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { _auto_gift: '1' } }],
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      return fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            // Out of stock / 422: fail silently and stop retrying this page view.
            this.addBlocked = true;
            return;
          }
          writeSession(ADDED_KEY, String(variantId));
          // /cart/add.js only returns the added items; the cart is certainly
          // not empty right after an add.
          this.renderSections(response.sections, 1);
          return publish(PUB_SUB_EVENTS.cartUpdate, { source: 'auto-gift', cartData: response });
        })
        .catch(() => {});
    }

    changeGift(lineKey, quantity) {
      const body = JSON.stringify({
        id: lineKey,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
        .then((response) => response.json())
        .then((response) => {
          if (response.errors || response.status) return;
          this.renderSections(response.sections, response.item_count);
          return publish(PUB_SUB_EVENTS.cartUpdate, { source: 'auto-gift', cartData: response });
        })
        .catch(() => {});
    }

    // Same shape as CartItems/CartDrawer.getSectionsToRender, limited to the
    // cart surfaces present on the current page.
    getSectionsToRender() {
      const sections = [
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];

      if (document.querySelector('cart-drawer')) {
        sections.push({
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '#CartDrawer',
        });
      }

      const mainCartItems = document.getElementById('main-cart-items');
      if (mainCartItems) {
        sections.push({
          id: 'main-cart-items',
          section: mainCartItems.dataset.id,
          selector: '.js-contents',
        });
      }

      const mainCartFooter = document.getElementById('main-cart-footer');
      if (mainCartFooter) {
        sections.push({
          id: 'main-cart-footer',
          section: mainCartFooter.dataset.id,
          selector: '.js-contents',
        });
      }

      return sections;
    }

    renderSections(sections, itemCount) {
      if (!sections) return;

      this.getSectionsToRender().forEach((section) => {
        const sectionHTML = sections[section.section];
        const container = document.getElementById(section.id);
        if (!sectionHTML || !container) return;

        const source = new DOMParser().parseFromString(sectionHTML, 'text/html').querySelector(section.selector);
        if (!source) return;

        const elementToReplace = container.querySelector(section.selector) || container;
        elementToReplace.innerHTML = source.innerHTML;
      });

      // Mirror CartItems.updateQuantity's empty-state handling.
      if (typeof itemCount === 'number') {
        const isEmpty = itemCount === 0;
        document.querySelectorAll('cart-items, cart-drawer-items').forEach((element) => {
          element.classList.toggle('is-empty', isEmpty);
        });
        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
      }

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        if (typeof itemCount === 'number') cartDrawer.classList.toggle('is-empty', itemCount === 0);
        // Replacing #CartDrawer wipes the overlay's click handler; rebind it
        // the same way CartDrawer.renderContents does. The drawer is left in
        // its current open/closed state on purpose.
        const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new AutoGift(), { once: true });
  } else {
    new AutoGift();
  }
})();
